import { Component, inject, OnInit } from '@angular/core';
import { NgClass } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthApiService } from '../../../../core/services/auth/auth-api.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { EMAIL_REGEX, MIN_PASSWORD_LENGTH, PASSWORD_REGEX } from '../../../user/constant/auth.constant';
import { passwordConfirmationValidator } from '../../../../core/validator/confirmPasswordValidator';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [NgClass, ReactiveFormsModule],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnInit {
  #fb = inject(FormBuilder);
  #authService = inject(AuthApiService);
  #router = inject(Router);
  #toast = inject(ToastrService);

  accountForm!: FormGroup;
  passwordForm!: FormGroup;

  submittedEmail = false;
  submittedPassword = false;

  ngOnInit(): void {
    this._initAccountForm();
    this._initPasswordForm();
    this._populateAccountForm();
  }

  private _initAccountForm(): void {
    this.accountForm = this.#fb.group({
      email: ['', [Validators.required, Validators.pattern(EMAIL_REGEX)]],
    });
  }

  private _initPasswordForm(): void {
    this.passwordForm = this.#fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH), Validators.pattern(PASSWORD_REGEX)]],
        confirmPassword: ['', Validators.required],
      },
      {
        validators: passwordConfirmationValidator('password', 'confirmPassword'),
      }
    );
  }

  private _populateAccountForm(): void {
    this.#authService.getUser$().subscribe(user => {
      if (user?.email) {
        this.accountForm.patchValue({
          email: user.email,
        });
      }
    });
  }

  get email(): AbstractControl {
    return this.accountForm.get('email')!;
  }

  get password(): AbstractControl {
    return this.passwordForm.get('password')!;
  }

  get confirmPassword(): AbstractControl {
    return this.passwordForm.get('confirmPassword')!;
  }

  get passwordMismatch(): boolean {
    return this.confirmPassword.hasError('passwordMismatch');
  }

  updateEmail(): void {
    this.submittedEmail = true;
    if (this.accountForm.invalid) return;

    const email = this.email.value;

    this.#authService.updateEmail$(email).subscribe({
      next: () => this.#toast.success('Email mis à jour avec succès !', 'Succès'),
      error: () => this.#toast.error('Erreur lors de la mise à jour de l’email.', 'Erreur'),
    });
  }

  updatePassword(): void {
    this.submittedPassword = true;
    if (this.passwordForm.invalid) return;

    const password = this.password.value;

    this.#authService.updatePassword$(password).subscribe({
      next: () => this.#toast.success('Mot de passe mis à jour avec succès !', 'Succès'),
      error: () => this.#toast.error('Erreur lors de la mise à jour du mot de passe.', 'Erreur'),
    });
  }

  deleteAccount(): void {
    if (confirm('Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible.')) {
      this.#authService.deleteAccount$().subscribe({
        next: () => {
          this.#toast.success('Compte supprimé avec succès.', 'Succès');
          this.#router.navigate(['']);
        },
        error: () => this.#toast.error('Erreur lors de la suppression du compte.', 'Erreur'),
      });
    }
  }
}
